import { useMemo } from "react"
import { Button, Select, Tag, Tooltip } from "antd"
import { AlertTriangle } from "lucide-react"
import { MAX_CATEGORICAL_SERIES, type TenantSummary } from "@csbi/contracts"
import { tenantColor, type ThemeMode } from "@csbi/viz-core"
import { FILTER_CONTROL_SIZE, FILTER_TENANT_MIN_WIDTH } from "../control-size.js"
import { ENV_LABELS, ENV_ORDER } from "../../../shared/env-labels.js"

export type TenantPickerProps = {
  tenants: readonly TenantSummary[]
  value: readonly string[]
  onChange: (next: string[]) => void
  mode: ThemeMode
  onSwitchCompareMode?: (next: "smallMultiples" | "table") => void
  loading?: boolean
}

export function TenantPicker({
  tenants,
  value,
  onChange,
  mode,
  onSwitchCompareMode,
  loading,
}: TenantPickerProps) {
  const options = useMemo(() => {
    const live = tenants.filter((t) => !t.churned)
    const churned = tenants.filter((t) => t.churned)
    const groups = ENV_ORDER.filter((env) => live.some((t) => t.environment === env)).map((env) => ({
      label: ENV_LABELS[env] ?? env,
      options: live.filter((t) => t.environment === env).map(toOption),
    }))
    if (churned.length) groups.push({ label: "Churned", options: churned.map(toOption) })
    return groups
  }, [tenants])

  const overCap = value.length > MAX_CATEGORICAL_SERIES

  return (
    <Select
      mode="multiple"
      // The size comes from the shared constant, never a literal here (see the comment in control-size.ts)
      size={FILTER_CONTROL_SIZE}
      allowClear
      loading={loading}
      value={[...value]}
      onChange={onChange}
      placeholder={`All customers (${tenants.length})`}
      maxTagCount="responsive"
      style={{ minWidth: FILTER_TENANT_MIN_WIDTH }}
      popupMatchSelectWidth={320}
      optionFilterProp="searchText"
      options={options}
      optionRender={(opt) => {
        const t = (opt.data as unknown as { tenant: TenantSummary }).tenant
        return (
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span
              aria-hidden="true"
              style={{
                width: 9,
                height: 9,
                flex: "0 0 auto",
                borderRadius: 999,
                background: tenantColor(t.colorSlot, mode),
              }}
            />
            <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}>{t.displayName}</span>
            {t.churned && <Tag style={{ marginInlineEnd: 0 }}>Churned</Tag>}
            {t.state === "stale" && (
              <Tooltip title="Collection is failing — this deployment's data may not be current">
                <AlertTriangle size={13} color="var(--csbi-status-warning-ink)" />
              </Tooltip>
            )}
          </span>
        )
      }}
      popupRender={(menu) => (
        <>
          <div style={{ display: "flex", gap: 4, padding: "6px 8px", borderBottom: "1px solid var(--csbi-gridline)" }}>
            <Button size="small" type="link" onClick={() => onChange(tenants.filter((t) => !t.churned).map((t) => t.id))}>
              Select all
            </Button>
            <Button size="small" type="link" onClick={() => onChange([])}>
              Clear
            </Button>
            <Button
              size="small"
              type="link"
              onClick={() => onChange(tenants.filter((t) => t.environment === "production" && !t.churned).map((t) => t.id))}
            >
              Production only
            </Button>
          </div>
          {menu}
          {overCap && (
            <div
              style={{
                borderTop: "1px solid var(--csbi-gridline)",
                padding: "8px 10px",
                fontSize: 12,
                lineHeight: 1.6,
                color: "var(--csbi-ink-secondary)",
              }}
            >
              {value.length} customers selected. Past {MAX_CATEGORICAL_SERIES}, colour can no longer tell them
              apart reliably, so charts will switch to{" "}
              <Button size="small" type="link" style={{ padding: 0 }} onClick={() => onSwitchCompareMode?.("smallMultiples")}>
                small multiples
              </Button>{" "}
              or a{" "}
              <Button size="small" type="link" style={{ padding: 0 }} onClick={() => onSwitchCompareMode?.("table")}>
                table
              </Button>
              .
            </div>
          )}
        </>
      )}
    />
  )
}

function toOption(t: TenantSummary) {
  return { value: t.id, label: t.displayName, searchText: `${t.displayName} ${t.id}`, tenant: t }
}
